import * as React from 'react';

import { ComposerPrimitive, ThreadPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { ArrowDownIcon, ArrowUpIcon, Mic, Square } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useEvent } from 'react-use';

import {
  COMPOSER_GHOST_CLICK_DEDUP_MS,
  PENDING_LANGUAGE_SWITCH_SEND_KEY,
} from '@/common/constants';
import { isTextPart } from '@/components/assistant-ui/message-part-utils';
import { isHistorySettled } from '@/components/assistant-ui/thread/auto-send-gate';
import { EarlyLoadStatus } from '@/components/assistant-ui/thread/early-load-status';
import { setTurnStopped } from '@/components/assistant-ui/thread/stopped-turns';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { useDevTools } from '@/components/providers/dev-tools-provider';
import { Button } from '@/components/ui/button';
import { LanguageSwitchDialog } from '@/components/ui/language-switch-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCharacterLimit } from '@/hooks/use-character-limit';
import { useClickClaim } from '@/hooks/use-click-claim';
import { useComposerFocusHold } from '@/hooks/use-composer-focus-hold';
import { useComposerInputHandlers } from '@/hooks/use-composer-input-handlers';
import { useComposerKeyboardFocus } from '@/hooks/use-composer-keyboard-focus';
import { useDictationHandlers } from '@/hooks/use-dictation-handlers';
import { useDictationLifecycle } from '@/hooks/use-dictation-lifecycle';
import { useDictationSupport } from '@/hooks/use-dictation-support';
import { useEarlyInputHandoff } from '@/hooks/use-early-input-handoff';
import {
  COMPOSER_INPUT_PROPS,
  findComposerInput,
  useFocusComposer,
} from '@/hooks/use-focus-composer';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useMessageLanguageDetection } from '@/hooks/use-message-language-detection';
import { useIsTouchDevice } from '@/hooks/use-mobile';
import { useResumableStreamCancel } from '@/hooks/use-resumable-stream-cancel';
import { useTrackEvent } from '@/hooks/use-track-event';
import { CHAT_ANALYTICS_EVENTS, WINDOW_EVENTS } from '@/lib/constants/post-message-types';
import { MIC_BUTTON_CLASS } from '@/lib/dictation/constants';
import type { DictationStatusType } from '@/lib/dictation/types';
import { safeSessionStorage } from '@/lib/storage/safe-storage';
import { useRequestStorageAccess } from '@/lib/storage/use-storage-access';
import { useSyncedComposer } from '@/lib/sync/hooks/use-synced-composer';
import { cn } from '@/lib/utils';
import { queueDeletion } from '@/lib/utils/deletion-queue';
import { changeLocale, markLocaleAsManuallyChosen } from '@/lib/utils/locale';

import { DisclaimerRow } from './disclaimer-row';
import { useParentComposerText } from './use-parent-composer-text';

const DevToolsRow = dynamic(
  () => import('./dev-tools-row').then(mod => ({ default: mod.DevToolsRow })),
  { ssr: false },
);

// Runs the language-switch check + UI side-effects as a single unit, so
// keydown and send-click callers stay in lockstep. Returns true when a
// language-switch prompt was shown and the caller should short-circuit.
function tryConsumeLanguageSwitch({
  event,
  text,
  inputRef,
  trackEvent,
  checkLanguageSwitch,
}: {
  event: React.SyntheticEvent;
  text: string;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  trackEvent: ReturnType<typeof useTrackEvent>;
  checkLanguageSwitch: (text: string) => boolean;
}): boolean {
  if (!checkLanguageSwitch(text)) return false;
  trackEvent({ event: CHAT_ANALYTICS_EVENTS.LANGUAGE_SWITCH_SHOWN });
  inputRef.current?.blur();
  event.preventDefault();
  event.stopPropagation();
  return true;
}

// Placeholder shown while DictationButton JS is loading (ssr: false means null on server)
export function DictationButtonLoading() {
  const t = useTranslations();
  // Named as the button it stands in for: disabled only through aria, it still takes focus.
  return (
    <TooltipIconButton
      tooltip={t('thread.tooltips.startDictation')}
      side="bottom"
      type="button"
      variant="ghost"
      size="icon"
      disabled
      className={cn('aui-composer-dictate', MIC_BUTTON_CLASS)}
      aria-label={t('thread.ariaLabels.startDictation')}
    >
      <Mic className="aui-composer-dictate-icon size-4" />
    </TooltipIconButton>
  );
}

// Lazy load DictationButton - only loaded when dictation is enabled for the tenant
const DictationButton = dynamic(
  () => import('./dictation-button').then(mod => ({ default: mod.DictationButton })),
  { ssr: false, loading: DictationButtonLoading },
);

// Clears the window handoff once the parent-set composer text has been consumed.
function clearPendingComposerText(): void {
  delete window.__pendingComposerText;
  delete window.__pendingComposerSubmit;
  delete window.__pendingComposerNewConversation;
  delete window.__pendingComposerFocus;
}

// True when an assistant message's run ended in error — the user-facing failure
// state surfaced by the message error UI and the retry button.
function isErroredAssistantResponse(message: {
  role: string;
  status?: { type?: string; reason?: string };
}): boolean {
  return (
    message.role === 'assistant' &&
    message.status?.type === 'incomplete' &&
    message.status?.reason === 'error'
  );
}

type ScrollToBottomButtonProps = {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  tooltip: string;
};

export const ScrollToBottomButton = React.forwardRef<HTMLButtonElement, ScrollToBottomButtonProps>(
  ({ disabled, onClick, tooltip }, ref) => (
    <m.div
      inert={disabled}
      // pointer-events-none covers browsers in our range that predate `inert` (Chrome <102, Safari <15.5)
      className={cn(
        'aui-thread-scroll-to-bottom absolute -top-12 z-10 self-center',
        disabled && 'pointer-events-none',
      )}
      initial={false}
      animate={disabled ? { opacity: 0, y: 8, scale: 0.9 } : { opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <TooltipIconButton
        ref={ref}
        onClick={onClick}
        tooltip={tooltip}
        disabled={disabled}
        side="top"
        variant="outline"
        className="rounded-full border-border p-4 dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </m.div>
  ),
);
ScrollToBottomButton.displayName = 'ScrollToBottomButton';

function ThreadScrollToBottom({ onClick }: { onClick?: () => void }) {
  const t = useTranslations();

  return (
    <ThreadPrimitive.ScrollToBottom behavior="smooth" onClick={onClick} asChild>
      <ScrollToBottomButton tooltip={t('thread.tooltips.scrollToBottom')} />
    </ThreadPrimitive.ScrollToBottom>
  );
}

export function Composer({
  onScrollToBottom,
  onSubmit: onSubmitProp,
  bootThreadId = null,
}: {
  onScrollToBottom?: () => void;
  onSubmit?: () => void;
  bootThreadId?: string | null;
}) {
  const t = useTranslations();
  const isTouchDevice = useIsTouchDevice();
  const { showDevTools } = useDevTools();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const composerInputPlaceholder = useLocalizedConfig<string>('chat.composerInputPlaceholder');
  const composerInputPlaceholderAfterFirstMessage = useLocalizedConfig<string>(
    'chat.composerInputPlaceholderAfterFirstMessage',
  );
  const { requestOnInteraction } = useRequestStorageAccess();
  const { checkAndPromptLanguageSwitch, languageSwitchState, clearLanguageSwitchState } =
    useMessageLanguageDetection();
  const trackEvent = useTrackEvent();
  const { showCharLimitError, triggerCharLimitError, handleInput, handlePaste, truncateToLimit } =
    useCharacterLimit();
  const api = useAui();
  const composerRuntime = api.composer;
  const thread = useAuiState(({ threadListItem }) => threadListItem);
  const showAfterFirstMessagePlaceholder = useAuiState(() => {
    const { messages, isLoading } = api.thread.getState();
    return messages.length > 0 || (!!thread.remoteId && isLoading);
  });
  const { composerText: syncedComposerText, setComposerText } = useSyncedComposer(
    thread.remoteId ?? thread.id,
  );

  const dictationStatus = useAuiState(
    ({ composer }) => composer.dictation?.status.type as DictationStatusType | undefined,
  );
  const composerText = useAuiState(({ composer }) => composer.text);
  const isRunning = useAuiState(({ thread }) => thread.isRunning);
  // Gate the auto-send effects behind history restore (see isHistorySettled).
  // Read via getState() since `isLoading` isn't on the slice type.
  const isThreadLoading = useAuiState(() => api.thread.getState().isLoading);

  // Rationale for the window length is on COMPOSER_GHOST_CLICK_DEDUP_MS.
  const { tryClaim: tryClaimSend } = useClickClaim(COMPOSER_GHOST_CLICK_DEDUP_MS);

  const { showDictation, isCheckingSupport, dictationProvider } = useDictationSupport();
  const threadId = thread.remoteId ?? thread.id;
  const {
    isDictating,
    startDictation,
    stopDictation,
    isRequestingMicPermission,
    isMicPermissionDenied,
    wasMicJustDenied,
    clearMicJustDenied,
  } = useDictationHandlers({ composerRuntime, threadId, dictationProvider });

  useDictationLifecycle({
    isDictating,
    composerText,
    composerRuntime,
    threadId,
    truncateToLimit,
    triggerCharLimitError,
    setComposerText,
  });

  const focusComposerInput = useFocusComposer();
  const focusComposer = React.useCallback(
    function focusComposer() {
      focusComposerInput({ input: inputRef.current });
    },
    [focusComposerInput],
  );

  const { isFocusHeld, isFocusHeldRef } = useComposerFocusHold({
    onOpenedWithFocus: focusComposer,
  });

  // Focus on initial load, unless the chat opened by itself
  React.useEffect(
    function focusOnFirstLoad() {
      if (!isFocusHeldRef.current) focusComposer();
    },
    [focusComposer, isFocusHeldRef],
  );

  // Set by applyComposerText when the parent requested an immediate send (CTA open).
  // Consumed by the auto-send effect once the runtime composer text reflects it.
  const autoSendPendingRef = React.useRef(false);

  // Latches once the thread's history load begins (see isHistorySettled).
  const sawThreadLoadingRef = React.useRef(false);
  React.useEffect(() => {
    if (isThreadLoading) sawThreadLoadingRef.current = true;
  }, [isThreadLoading]);

  // Detects a CTA submit that repeats the thread's last user message so we don't
  // resend an unchanged form. Returns null when it's not a repeat (caller should
  // send), or the repeat state otherwise — `erroredMessageId` is set when the prior
  // response failed, so the caller can retry it instead of silently doing nothing.
  const getDuplicateSubmissionState = React.useCallback(
    (text: string): { erroredMessageId?: string } | null => {
      const { messages, isRunning, isLoading } = api.thread.getState();

      const lastUser = messages.findLast(message => message.role === 'user');
      if (!lastUser) return null;

      const lastUserText = lastUser.content
        .filter(isTextPart)
        .map(part => part.text)
        .join('')
        .trim();
      if (lastUserText !== text.trim()) return null;

      // Still answering the same text.
      if (isRunning || isLoading) return {};

      // After a settled run the response is the last message; if that's still the
      // user message, there's no response yet — allow a resend.
      const response = messages.at(-1);
      if (response?.role !== 'assistant') return null;
      return isErroredAssistantResponse(response) ? { erroredMessageId: response.id } : {};
    },
    [api],
  );

  // Retries a failed assistant response, mirroring the message action bar's reload:
  // queue the errored message for deletion, then regenerate from its parent.
  const retryErroredResponse = React.useCallback(
    async (messageId: string) => {
      const localThreadId = thread?.id;
      if (localThreadId) await queueDeletion(localThreadId, messageId);
      api.thread.message({ id: messageId }).reload();
    },
    [api, thread],
  );

  // Handle composer text set from parent (e.g., form data extraction)
  // Uses API directly to avoid persisting to local storage
  const applyComposerText = React.useCallback(
    function applyComposerText({
      text,
      submit,
      shouldFocus,
    }: {
      text: string;
      submit: boolean;
      shouldFocus: boolean;
    }) {
      if (submit && text) {
        const duplicate = getDuplicateSubmissionState(text);
        if (duplicate) {
          // CTA reopened with the same form: don't resend. If the previous response
          // errored, retry it (preferred); otherwise just open the widget.
          if (duplicate.erroredMessageId) {
            void retryErroredResponse(duplicate.erroredMessageId);
          }
          return;
        }
      }
      api.composer.setText(text);
      if (shouldFocus) focusComposer();
      if (submit && text) {
        autoSendPendingRef.current = true;
      }
    },
    [api, focusComposer, getDuplicateSubmissionState, retryErroredResponse],
  );

  const getMessageCount = React.useCallback(
    function getMessageCount() {
      return api.thread.getState().messages.length;
    },
    [api],
  );
  const switchToNewThread = React.useCallback(
    function switchToNewThread() {
      return api.threads.switchToNewThread();
    },
    [api],
  );
  const applyParentComposerText = useParentComposerText({
    thread,
    getMessageCount,
    switchToNewThread,
    applyComposerText,
  });

  // Check for pending text on initial mount (text may arrive before this component mounts)
  React.useEffect(() => {
    if (window.__pendingComposerText !== undefined) {
      applyParentComposerText({
        text: window.__pendingComposerText,
        submit: window.__pendingComposerSubmit === true,
        shouldFocus: window.__pendingComposerFocus !== false,
        isNewConversation: window.__pendingComposerNewConversation === true,
      });
      clearPendingComposerText();
    }
  }, [applyParentComposerText]);

  // Handle text set after mount (empty string clears the composer)
  useEvent(WINDOW_EVENTS.SET_COMPOSER_TEXT, (e: Event) => {
    const { text, submit, newConversation, focus } = (
      e as CustomEvent<{
        text: string;
        submit?: boolean;
        newConversation?: boolean;
        focus?: boolean;
      }>
    ).detail;
    if (text !== undefined) {
      applyParentComposerText({
        text,
        submit: submit === true,
        shouldFocus: focus !== false,
        isNewConversation: newConversation === true,
      });
      // Clear the pending text since we handled it via event
      clearPendingComposerText();
    }
  });

  useComposerKeyboardFocus({ inputRef, isTouchDevice });

  const historySettled = React.useCallback(
    () =>
      isHistorySettled({
        remoteId: thread.remoteId,
        isThreadLoading,
        sawThreadLoading: sawThreadLoadingRef.current,
      }),
    [thread.remoteId, isThreadLoading],
  );

  const { heldText: seededText, setVisitorText } = useEarlyInputHandoff({
    inputRef,
    setComposerText,
    composerText,
    draftText: syncedComposerText,
    historySettled,
    remoteId: thread.remoteId,
    bootThreadId,
    onTextTooLong: triggerCharLimitError,
  });

  const { handleTextChange: onTextChange, handlePasteEvent } = useComposerInputHandlers({
    handleInput,
    handlePaste,
    setComposerText: setVisitorText,
    inputRef,
  });

  const handleTextChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      if (isDictating) {
        stopDictation();
      }
      onTextChange(e);
    },
    [isDictating, stopDictation, onTextChange],
  );

  // Clear persisted composer text synchronously before the send executes.
  // Using fromUserInput to avoid resetting the runtime's text (needed for the send).
  const clearComposerStore = React.useCallback(() => {
    setComposerText('', { fromUserInput: true });
  }, [setComposerText]);

  const onBeforeSend = React.useCallback(() => {
    onSubmitProp?.();
    clearComposerStore();
    if (isTouchDevice) {
      inputRef.current?.blur();
    }
  }, [onSubmitProp, clearComposerStore, isTouchDevice]);

  const performSend = React.useCallback(() => {
    if (!tryClaimSend()) return;
    onBeforeSend();
    composerRuntime.send();
  }, [tryClaimSend, onBeforeSend, composerRuntime]);

  // Auto-send when the parent applied composer text with a submit intent (CTA open),
  // once the runtime text reflects it and history import has settled.
  React.useEffect(() => {
    if (!autoSendPendingRef.current || !composerText || !historySettled()) return;
    autoSendPendingRef.current = false;
    performSend();
  }, [composerText, performSend, historySettled]);

  // After a locale-switch reload, auto-send the restored synced text once history
  // import settles. Clear the pending flag only on send, so a deferred send keeps it.
  React.useEffect(() => {
    if (!syncedComposerText || !safeSessionStorage.getItem(PENDING_LANGUAGE_SWITCH_SEND_KEY)) {
      return;
    }
    if (!historySettled()) return;
    safeSessionStorage.removeItem(PENDING_LANGUAGE_SWITCH_SEND_KEY);
    performSend();
  }, [syncedComposerText, performSend, historySettled]);

  const handleLanguageConfirm = React.useCallback(() => {
    if (!languageSwitchState) return;
    trackEvent({
      event: CHAT_ANALYTICS_EVENTS.LANGUAGE_SWITCH_DECIDED,
      properties: {
        action: 'switched',
        detectedLocale: languageSwitchState.detectedLocale,
        currentLocale: languageSwitchState.currentLocale,
      },
    });
    safeSessionStorage.setItem(PENDING_LANGUAGE_SWITCH_SEND_KEY, 'true');
    changeLocale(languageSwitchState.detectedLocale);
  }, [languageSwitchState, trackEvent]);

  const handleLanguageDismiss = React.useCallback(() => {
    if (languageSwitchState) {
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.LANGUAGE_SWITCH_DECIDED,
        properties: {
          action: 'kept',
          detectedLocale: languageSwitchState.detectedLocale,
          currentLocale: languageSwitchState.currentLocale,
        },
      });
      markLocaleAsManuallyChosen(languageSwitchState.currentLocale);
    }
    clearLanguageSwitchState();
    performSend();
  }, [languageSwitchState, trackEvent, clearLanguageSwitchState, performSend]);

  const handleSubmit = React.useCallback(
    (e: React.SyntheticEvent) => {
      // Backup gate for keyboard-driven submits (e.g. the Enter-key path
      // that doesn't go through the Send button's onClick). The PRIMARY
      // dedupe for ghost-clicks lives in `handleSendClick` below.
      if (
        tryConsumeLanguageSwitch({
          event: e,
          text: inputRef.current?.value || '',
          inputRef,
          trackEvent,
          checkLanguageSwitch: checkAndPromptLanguageSwitch,
        })
      ) {
        return;
      }

      if (!tryClaimSend()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onBeforeSend();
    },
    [tryClaimSend, checkAndPromptLanguageSwitch, trackEvent, onBeforeSend],
  );

  const inputContent = (
    <Tooltip open={showCharLimitError}>
      <TooltipTrigger asChild>
        <ComposerPrimitive.Input
          ref={inputRef}
          {...COMPOSER_INPUT_PROPS}
          {...(seededText !== undefined && { value: seededText })}
          onInput={handleTextChange}
          onPaste={handlePasteEvent}
          placeholder={
            showAfterFirstMessagePlaceholder && composerInputPlaceholderAfterFirstMessage
              ? composerInputPlaceholderAfterFirstMessage
              : composerInputPlaceholder || t('thread.placeholders.composerInput')
          }
          onFocus={requestOnInteraction}
          unstable_focusOnRunStart={!isFocusHeld}
          unstable_focusOnThreadSwitched={!isFocusHeld}
          unstable_focusOnScrollToBottom={!isFocusHeld}
          submitMode={isTouchDevice ? 'none' : 'enter'}
          className={cn(
            'aui-composer-input max-h-32 w-full resize-none content-center bg-transparent text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed',
            showDevTools
              ? 'mb-1 min-h-16 content-start px-3.5 pe-3.5 pt-1.5 pb-3'
              : 'min-h-12 px-3 py-2 md:mb-1 md:min-h-16 md:content-start md:px-3.5 md:pe-3.5 md:pt-1.5 md:pb-3',
          )}
          rows={1}
          aria-label={t('thread.ariaLabels.messageInput')}
        />
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        {t('characterLimit.exceeded')}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <div
      className={cn(
        'aui-composer-wrapper sticky bottom-0 z-30 mx-auto flex w-full max-w-[var(--thread-max-width)] flex-col overflow-visible bg-background',
        'gap-1.5 rounded-t-3xl pb-1.5',
        'md:gap-2 md:pb-3',
      )}
    >
      <ThreadScrollToBottom onClick={onScrollToBottom} />
      <ComposerPrimitive.Root
        onSubmit={handleSubmit}
        className={cn(
          'aui-composer-root relative flex w-full flex-col border border-border bg-muted shadow-[0_9px_9px_0px_rgba(0,0,0,0.01),0_2px_5px_0px_rgba(0,0,0,0.06)] dark:border-muted-foreground/15',
          // The input draws no outline of its own, so the box it sits in shows where focus is.
          'has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-ring',
          'rounded-2xl px-0.5 py-1',
          'md:rounded-3xl md:px-1',
          showCharLimitError ? 'border-destructive' : undefined,
        )}
      >
        <div
          className={cn(
            'aui-composer-input-container relative md:contents',
            showDevTools ? 'contents' : 'flex items-end',
          )}
        >
          {inputContent}
          <ComposerAction
            inputRef={inputRef}
            isRunning={isRunning}
            dictationStatus={dictationStatus}
            isCheckingSupport={isCheckingSupport}
            isRequestingMicPermission={isRequestingMicPermission}
            isMicPermissionDenied={isMicPermissionDenied}
            wasMicJustDenied={wasMicJustDenied}
            onClearMicJustDenied={clearMicJustDenied}
            showDictation={showDictation}
            dictationProvider={dictationProvider}
            onStartDictation={startDictation}
            onStopDictation={stopDictation}
            onBeforeSend={onBeforeSend}
            checkLanguageSwitch={checkAndPromptLanguageSwitch}
            tryClaimSend={tryClaimSend}
          />
        </div>
      </ComposerPrimitive.Root>
      <EarlyLoadStatus />
      <DisclaimerRow />
      {languageSwitchState && (
        <LanguageSwitchDialog
          detectedLocale={languageSwitchState.detectedLocale}
          currentLocale={languageSwitchState.currentLocale}
          onConfirm={handleLanguageConfirm}
          onDismiss={handleLanguageDismiss}
        />
      )}
    </div>
  );
}

interface ComposerActionProps {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  isRunning: boolean;
  dictationStatus: DictationStatusType | undefined;
  isCheckingSupport: boolean;
  isRequestingMicPermission: boolean;
  isMicPermissionDenied: boolean;
  wasMicJustDenied: boolean;
  onClearMicJustDenied: () => void;
  showDictation: boolean;
  dictationProvider: 'browser' | 'elevenlabs';
  onStartDictation: () => void;
  onStopDictation: () => void;
  onBeforeSend: () => void;
  checkLanguageSwitch: (text: string) => boolean;
  tryClaimSend: () => boolean;
}

function ComposerAction({
  inputRef,
  isRunning,
  dictationStatus,
  isCheckingSupport,
  isRequestingMicPermission,
  isMicPermissionDenied,
  wasMicJustDenied,
  onClearMicJustDenied,
  showDictation,
  dictationProvider,
  onStartDictation,
  onStopDictation,
  onBeforeSend,
  checkLanguageSwitch,
  tryClaimSend,
}: ComposerActionProps) {
  const t = useTranslations();
  const trackEvent = useTrackEvent();
  const { requestOnInteraction } = useRequestStorageAccess();

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleSendClick = React.useCallback(
    (e: React.MouseEvent) => {
      // The actual `composerRuntime.send()` is wired by assistant-ui's
      // `<ComposerPrimitive.Send>` via `composeEventHandlers(onClick, send)`
      // — `send()` runs immediately after this handler unless we
      // `preventDefault()`. The form's `onSubmit` runs LATER (and not at
      // all when `createActionButton` forces `type="button"`), so this is
      // the synchronous boundary that must dedupe Android ghost-clicks
      // (root cause of PostHog issue 019e1855: every duplicate
      // `user_message_sent` pair fired within 1–66ms). `tryClaimSend()`
      // returns false within `COMPOSER_GHOST_CLICK_DEDUP_MS` of the previous call.
      const inputValue = inputRef.current?.value || '';

      if (
        tryConsumeLanguageSwitch({
          event: e,
          text: inputValue,
          inputRef,
          trackEvent,
          checkLanguageSwitch,
        })
      ) {
        return;
      }

      if (!tryClaimSend()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      onBeforeSend();
      requestOnInteraction();
    },
    [inputRef, checkLanguageSwitch, trackEvent, onBeforeSend, requestOnInteraction, tryClaimSend],
  );

  const { showDevTools } = useDevTools();

  return (
    <div
      className={cn(
        'aui-composer-action-wrapper flex flex-row-reverse items-center gap-1',
        showDevTools
          ? 'relative mx-1 mt-2 mb-2 justify-between self-auto py-0 pe-0'
          : 'self-end py-2 pe-1 @3xl:relative @3xl:mx-1 @3xl:mt-2 @3xl:mb-2 @3xl:justify-between @3xl:self-auto @3xl:py-0 @3xl:pe-0',
      )}
    >
      {/* Mic + primary are grouped so the wrapper's `justify-between` spreads this
          group against DevToolsRow, not the two buttons against each other. */}
      <div className="flex flex-row-reverse items-center gap-1">
        <AnimatePresence mode="wait" initial={false}>
          {isRunning ? (
            <m.div
              key="cancel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <CancelButton />
            </m.div>
          ) : (
            <m.div
              key="send"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {/* Not disabled while dictating: send() cancels the session first, so
                this submits the committed transcript mid-speech. */}
              {/* No `disabled` prop: the primitive derives it from `canSend`, which
                also covers in-flight sends and runtime-level send locks. */}
              <ComposerPrimitive.Send asChild>
                <TooltipIconButton
                  type="submit"
                  onMouseDown={handleMouseDown}
                  onClick={handleSendClick}
                  tooltip={t('thread.ariaLabels.sendMessage')}
                  side="bottom"
                  variant="default"
                  size="icon"
                  className="aui-composer-send relative size-[30px] rounded-full p-1 @3xl:size-[34px]"
                  aria-label={t('thread.ariaLabels.sendMessage')}
                  // The early-input script sets its aria-disabled before hydration, as typing
                  // makes it usable (lib/early-input/bootstrap.ts).
                  suppressHydrationWarning
                >
                  <ArrowUpIcon className="aui-composer-send-icon size-4 @3xl:size-5" />
                </TooltipIconButton>
              </ComposerPrimitive.Send>
            </m.div>
          )}
        </AnimatePresence>
        {showDictation && (
          <DictationButton
            dictationStatus={dictationStatus}
            isCheckingSupport={isCheckingSupport}
            isRequestingMicPermission={isRequestingMicPermission}
            isMicPermissionDenied={isMicPermissionDenied}
            wasMicJustDenied={wasMicJustDenied}
            onClearMicJustDenied={onClearMicJustDenied}
            dictationProvider={dictationProvider}
            onStartDictation={onStartDictation}
            onStopDictation={onStopDictation}
          />
        )}
      </div>
      {showDevTools && (
        <div className="flex max-w-full min-w-0 flex-1 items-center justify-start gap-1 overflow-hidden">
          <DevToolsRow />
        </div>
      )}
    </div>
  );
}

function lastAssistantTurn(): HTMLElement | undefined {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-role="assistant"]')).at(-1);
}

export function CancelButton() {
  const t = useTranslations();
  // Resumable tenants: the server run outlives the client fetch by design, so the primitive's
  // cancel (fetch abort) alone would leave the model generating — this also stops it server-side.
  const cancelServerRun = useResumableStreamCancel();
  const api = useAui();
  const focusComposer = useFocusComposer();

  function stop(event: React.MouseEvent<HTMLButtonElement>) {
    const hadFocus = event.currentTarget === document.activeElement;
    const turn = api.thread.getState().messages.at(-1);
    if (turn?.role === 'assistant') setTurnStopped({ messageId: turn.id, stopped: true });
    cancelServerRun();
    // Send replaces this button once the run stops, and would leave focus on the page body.
    if (hadFocus) {
      focusComposer({
        input: findComposerInput(),
        onTouch: lastAssistantTurn(),
      });
    }
  }

  return (
    <ComposerPrimitive.Cancel onClick={stop} asChild>
      <Button
        type="button"
        variant="default"
        size="icon"
        className="aui-composer-cancel size-[30px] rounded-full @3xl:size-[34px]"
        aria-label={t('thread.ariaLabels.stopGenerating')}
      >
        <Square className="aui-composer-cancel-icon size-3 fill-current @3xl:size-3.5" />
      </Button>
    </ComposerPrimitive.Cancel>
  );
}
